import { Injectable, inject } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from '@angular/fire/auth';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  Firestore,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import {
  allowedEmailDomainsLabel,
  isAllowedCompanyEmail,
} from '../utils/company-details';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public auth = inject(Auth);
  private firestore = inject(Firestore);

  async getCompanyById(companyId: string): Promise<any | null> {
    const ref = doc(this.firestore, 'companies', companyId);
    const snap = await getDoc(ref);

    return snap.exists()
      ? { id: snap.id, ...snap.data() }
      : null;
  }

  async createAuditLog(log: any) {
    const ref = collection(this.firestore, 'user_audit_logs');

    return addDoc(ref, {
      ...log,
      createdAt: serverTimestamp(),
    });
  }

  async register(email: string, password: string, name: string) {
    const safeEmail = String(email || '').trim().toLowerCase();
    const safeName = String(name || '').trim();

    if (!safeEmail || !password || !safeName) {
      throw new Error('Name, email and password are required.');
    }

    if (!isAllowedCompanyEmail(safeEmail)) {
      throw new Error(
        `Only official company emails are allowed: ${allowedEmailDomainsLabel()}.`,
      );
    }

    const userCredential = await createUserWithEmailAndPassword(
      this.auth,
      safeEmail,
      password,
    );

    const uid = userCredential.user.uid;

    try {
      await setDoc(doc(this.firestore, 'users', uid), {
        uid,
        name: safeName,
        email: safeEmail,
        role: 'user',
        status: 'notApproved',
        permissions: {},
        profileImage: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      await signOut(this.auth).catch(() => undefined);
      throw error;
    }

    await signOut(this.auth);

    return userCredential;
  }

  async login(email: string, password: string) {
    const safeEmail = String(email || '').trim().toLowerCase();

    if (!isAllowedCompanyEmail(safeEmail)) {
      throw new Error(
        `Only official company emails are allowed: ${allowedEmailDomainsLabel()}.`,
      );
    }

    const credential = await signInWithEmailAndPassword(
      this.auth,
      safeEmail,
      password,
    );

    const uid = credential.user.uid;
    const userRef = doc(this.firestore, 'users', uid);
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      await signOut(this.auth).catch(() => undefined);
      throw new Error('User record not found.');
    }

    const userData: any = snap.data();
    const status = String(userData?.status || '').trim().toLowerCase();

    if (!isAllowedCompanyEmail(userData?.email || safeEmail) || status !== 'approved') {
      await signOut(this.auth).catch(() => undefined);
      throw new Error('Your account is not approved or email domain is not allowed.');
    }

    return credential;
  }

  logout() {
    return signOut(this.auth);
  }

  getCurrentUserProfile(): Observable<any | null> {
    return new Observable((observer) => {
      const unsubscribe = onAuthStateChanged(this.auth, async (user) => {
        if (!user) {
          observer.next(null);
          return;
        }

        try {
          const userRef = doc(this.firestore, 'users', user.uid);
          const userSnap = await getDoc(userRef);

          observer.next(userSnap.exists() ? userSnap.data() : null);
        } catch (error) {
          console.error('Error fetching profile:', error);
          observer.error(error);
        }
      });

      return () => unsubscribe();
    });
  }

  async saveUserProfile(profileData: any): Promise<void> {
    const user = this.auth.currentUser;

    if (!user) {
      throw new Error('No user is logged in');
    }

    const userRef = doc(this.firestore, 'users', user.uid);
    const docSnap = await getDoc(userRef);

    const existingData: any = docSnap.exists() ? docSnap.data() : {};
    const hasProfileImageUpdate = Object.prototype.hasOwnProperty.call(
      profileData || {},
      'profileImage',
    );
    const previousProfileImage = String(existingData?.profileImage || '');
    const nextProfileImage = String(profileData?.profileImage || '');
    const profileImageChanged =
      hasProfileImageUpdate && previousProfileImage !== nextProfileImage;

    const safeProfileData: any = {
      ...profileData,
      updatedAt: serverTimestamp(),
    };

    if (profileImageChanged) {
      Object.assign(safeProfileData, {
        profileUpdatedAt: serverTimestamp(),
        faceDescriptor: deleteField(),
        descriptor: deleteField(),
        faceDescriptors: deleteField(),
        faceDescriptorImageFingerprint: deleteField(),
        faceDescriptorUpdatedAt: deleteField(),
      });

      localStorage.removeItem(`portus_face_descriptor_${user.uid}`);

      try {
        const localUser = JSON.parse(localStorage.getItem('userData') || '{}');
        delete localUser.faceDescriptor;
        delete localUser.descriptor;
        delete localUser.faceDescriptors;
        delete localUser.faceDescriptorImageFingerprint;
        localStorage.setItem(
          'userData',
          JSON.stringify({
            ...localUser,
            profileImage: nextProfileImage,
          }),
        );
      } catch {
        localStorage.removeItem('userData');
      }
    }

    if (docSnap.exists()) {
      await updateDoc(userRef, safeProfileData);
      return;
    }

    await setDoc(userRef, {
      uid: user.uid,
      email: user.email || '',
      role: 'user',
      status: 'notApproved',
      permissions: {},
      profileImage: '',
      createdAt: serverTimestamp(),
      ...safeProfileData,
    });
  }
}
